import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getApiHeaders, API_TYPES } from '../utils/apiHeaders';

const DB_API_BASE_URL = import.meta.env.VITE_DB_API_URL || '/api';
const EMBED_API_URL = import.meta.env.VITE_EMBED_API_URL || 'https://pbie-embed-func.azurewebsites.net';
const EMBED_API_KEY = import.meta.env.VITE_EMBED_API_KEY || '';
const EMBED_API_SCOPE = import.meta.env.VITE_EMBED_API_SCOPE || 'api://b188d547-4145-4ad8-b803-562c061142ba/.default';

const CustomerEmbeddingManagement = ({ subscriptionData, isSuspended = false }) => {
const { getAccessToken } = useAuth();
const [settings, setSettings] = useState(null);
const [loading, setLoading] = useState(false);
const [loadingOperation, setLoadingOperation] = useState(null); // Track what operation is in progress
const [error, setError] = useState(null);
const [showSecret, setShowSecret] = useState(false);
const [tempSecret, setTempSecret] = useState(null); // For newly generated secrets
const [newOrigin, setNewOrigin] = useState('');
const [showCodeExamples, setShowCodeExamples] = useState(false);
  
// Helper functions for secret expiry display
const calculateDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const formatExpiryDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
};

const getExpiryStatusColor = (daysUntilExpiry) => {
  if (daysUntilExpiry === null) return '#6c757d'; // Gray
  if (daysUntilExpiry <= 7) return '#dc3545'; // Red - Urgent
  if (daysUntilExpiry <= 14) return '#fd7e14'; // Orange - Warning
  if (daysUntilExpiry <= 30) return '#ffc107'; // Yellow - Attention
  return '#28a745'; // Green - OK
};
  
// Toast notification state
const [toast, setToast] = useState({ show: false, message: '', type: '' });
  
// Modal states
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  
// Toast helpers
const showToast = (message, type = 'info') => {
  setToast({ show: true, message, type });
  setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
};

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [subscriptionData]);

  const loadSettings = async () => {
    if (!subscriptionData) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const headers = getApiHeaders(token, API_TYPES.DATABASE);
      
      const response = await fetch(
        `${DB_API_BASE_URL}/customer-embed-settings?subscriptionId=${subscriptionData.subscriptionID}`,
        {
          method: 'GET',
          headers
        }
      );

      if (response.status === 404) {
        // No settings yet - this is okay
        setSettings(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load settings: ${response.statusText}`);
      }

      const data = await response.json();
      setSettings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createAppRegistration = async () => {
    if (!subscriptionData) {
      return;
    }

    // Use MarketplaceSubscriptionId as the app name (handle both cases)
    const appName = subscriptionData.MarketplaceSubscriptionId || 
                    subscriptionData.marketplaceSubscriptionId || 
                    subscriptionData.SubscriptionID || 
                    subscriptionData.subscriptionID;
    
    if (!appName) {
      showToast('Unable to determine subscription ID. Please try again.', 'error');
      return;
    }
    
    await handleCreateAppWithName(appName);
  };
  
  const handleCreateAppWithName = async (appName) => {
    // Validate appName is a string and not empty
    if (!appName || typeof appName !== 'string' || !appName.trim()) {
      showToast('Invalid subscription information. Please refresh the page and try again.', 'error');
      return;
    }

    setLoading(true);
    setLoadingOperation('create');
    setError(null);

    try {
      const token = await getAccessToken();

      const requestBody = {
        subscriptionID: subscriptionData.subscriptionID,
        appDisplayName: appName,
        allowedOrigins: JSON.stringify([]) // Start with empty origins
      };

      showToast('Creating app registration... This may take 10-15 seconds.', 'info');

      // Call EMBED Function to create app registration
      // Uses code= query parameter for function key authentication (same as DB Function)
      const codeParam = EMBED_API_KEY ? `?code=${encodeURIComponent(EMBED_API_KEY)}` : '';
      const response = await fetch(
        `${EMBED_API_URL}/customer-apps${codeParam}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      console.log('[CustomerEmbedding] createAppRegistration: Response received', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        ok: response.ok
      });

      if (!response.ok) {
        // Try to parse error response as JSON, but handle empty/invalid responses
        let errorMessage = 'Failed to create app registration';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || `Server error: ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          // If we can't parse the error, use a generic message with status
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Store the secret temporarily (it won't be shown again!)
      setTempSecret(data.clientSecret);
      
      // Reload settings
      await loadSettings();
    } catch (err) {
      setError(err.message);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
      setLoadingOperation(null);
    }
  };

  const deleteAppRegistration = async () => {
    // Show confirmation modal
    setShowDeleteConfirm(true);
  };
  
  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);

    // Check if we have the app object ID and tenant ID
    if (!settings?.entraAppObjectId) {
      showToast('Unable to delete app registration: App information is missing. Please try refreshing the page.', 'error');
      return;
    }

    if (!settings?.entraAppTenantId) {
      showToast('Unable to delete app registration: Tenant information is missing. Please try refreshing the page.', 'error');
      return;
    }

    setLoading(true);
    setLoadingOperation('delete');
    setError(null);

    try {
      const token = await getAccessToken();
      
      // Call EMBED Function to delete app registration
      // Pass tenant ID in query string so it deletes from the correct tenant
      const codeParam = EMBED_API_KEY ? `?code=${encodeURIComponent(EMBED_API_KEY)}` : '';
      const tenantParam = `${codeParam ? '&' : '?'}tenantId=${encodeURIComponent(settings.entraAppTenantId)}`;
      const response = await fetch(
        `${EMBED_API_URL}/customer-apps/${settings.entraAppObjectId}${codeParam}${tenantParam}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('[CustomerEmbedding] deleteAppRegistration: Response received', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        ok: response.ok
      });

      if (!response.ok) {
        // Try to parse error response as JSON, but handle empty/invalid responses
        let errorMessage = 'Failed to delete app registration';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || `Server error: ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      setSettings(null);
      setTempSecret(null);
    } catch (err) {
      setError(err.message);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
      setLoadingOperation(null);
    }
  };

  const regenerateSecret = async () => {
    // Show confirmation modal
    setShowRegenerateConfirm(true);
  };
  
  const handleConfirmRegenerate = async () => {
    setShowRegenerateConfirm(false);

    // Check if we have the app object ID
    if (!settings?.entraAppObjectId) {
      showToast('Unable to regenerate secret: App registration information is missing. Please try refreshing the page.', 'error');
      return;
    }

    setLoading(true);
    setLoadingOperation('regenerate');
    setError(null);

    try {
      const token = await getAccessToken();
      
      // Call EMBED Function to regenerate secret
      // Uses entraAppObjectId in the URL path and code= query parameter for authentication
      const codeParam = EMBED_API_KEY ? `?code=${encodeURIComponent(EMBED_API_KEY)}` : '';
      const response = await fetch(
        `${EMBED_API_URL}/customer-apps/${settings.entraAppObjectId}/regenerate-secret${codeParam}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('[CustomerEmbedding] regenerateSecret: Response received', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        ok: response.ok
      });

      if (!response.ok) {
        // Try to parse error response as JSON, but handle empty/invalid responses
        let errorMessage = 'Failed to regenerate secret';
        let responseBody = '';
        
        try {
          const contentType = response.headers.get('content-type');
          
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            responseBody = JSON.stringify(errorData);
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            responseBody = errorText;
            errorMessage = errorText || `Server error: ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        
        // If we got an empty response, provide more context
        if (!responseBody || responseBody.trim() === '') {
          errorMessage = `Server returned ${response.status} ${response.statusText} with no error details. Please check the Azure Function logs for more information.`;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      setTempSecret(data.clientSecret);
    } catch (err) {
      setError(err.message);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
      setLoadingOperation(null);
    }
  };

  const updateSettings = async (updates) => {

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();

      const requestBody = {
        subscriptionID: subscriptionData.subscriptionID,
        entraAppObjectId: settings?.entraAppObjectId,
        entraAppClientId: settings?.entraAppClientId,
        entraAppDisplayName: settings?.entraAppDisplayName,
        ...updates
      };

      // Call DB Function with PUT to update settings (CORS only)
      const response = await fetch(
        `${DB_API_BASE_URL}/customer-embed-settings`,
        {
          method: 'PUT',
          headers: getApiHeaders(token, API_TYPES.DATABASE),
          body: JSON.stringify(requestBody)
        }
      );

      console.log('[CustomerEmbedding] updateSettings: Response received', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        ok: response.ok
      });

      if (!response.ok) {
        // Try to parse error response as JSON, but handle empty/invalid responses
        let errorMessage = 'Failed to update settings';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || `Server error: ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Don't show toast notification - settings updated silently
      await loadSettings();
    } catch (err) {
      setError(err.message);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addOrigin = () => {
    if (!newOrigin.trim()) {
      showToast('Please enter a valid origin', 'error');
      return;
    }

    if (!newOrigin.startsWith('https://') && !newOrigin.startsWith('http://localhost')) {
      showToast('Origins must use HTTPS (or http://localhost for development)', 'error');
      return;
    }

    try {
      const currentOrigins = settings?.allowedOrigins ? JSON.parse(settings.allowedOrigins) : [];
      
      if (currentOrigins.includes(newOrigin)) {
        showToast('This origin is already in the list', 'error');
        return;
      }

      const updatedOrigins = [...currentOrigins, newOrigin];

      updateSettings({
        allowedOrigins: JSON.stringify(updatedOrigins),
        isEnabled: settings?.isEnabled ?? true
      });

      setNewOrigin('');
    } catch (err) {
      showToast('Invalid origins format', 'error');
    }
  };

  const removeOrigin = (origin) => {
    try {
      const currentOrigins = JSON.parse(settings.allowedOrigins);
      
      const updatedOrigins = currentOrigins.filter(o => o !== origin);
      
      updateSettings({
        allowedOrigins: JSON.stringify(updatedOrigins),
        isEnabled: settings.isEnabled
      });
    } catch (err) {
      showToast('Failed to remove origin', 'error');
    }
  };

  const toggleEnabled = async () => {
    const newEnabledState = !settings?.isEnabled;

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();

      const requestBody = {
        subscriptionID: subscriptionData.subscriptionID,
        entraAppObjectId: settings?.entraAppObjectId,
        entraAppClientId: settings?.entraAppClientId,
        entraAppDisplayName: settings?.entraAppDisplayName,
        allowedOrigins: settings?.allowedOrigins,
        isEnabled: newEnabledState
      };

      // Call DB Function with PUT to update settings (CORS only)
      const response = await fetch(
        `${DB_API_BASE_URL}/customer-embed-settings`,
        {
          method: 'PUT',
          headers: getApiHeaders(token, API_TYPES.DATABASE),
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to update settings';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || `Server error: ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Don't show toast notification - just reload settings
      await loadSettings();
    } catch (err) {
      setError(err.message);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const hasAppRegistration = settings?.entraAppClientId;
  const allowedOrigins = settings?.allowedOrigins ? JSON.parse(settings.allowedOrigins) : [];
  const daysUntilExpiry = calculateDaysUntilExpiry(settings?.secretExpirationDate);
  const showExpiryWarning = daysUntilExpiry !== null && daysUntilExpiry <= 30;

  return (
    <div className="customer-embedding-management">
      <h2>Embedding</h2>
      <p style={{ marginBottom: '2rem' }}>
        Configure OAuth2 authentication and CORS settings to embed reports in your own applications.
      </p>

      {/* Secret Expiry Warning Banner */}
      {hasAppRegistration && settings?.secretExpirationDate && showExpiryWarning && (() => {
        const isUrgent = daysUntilExpiry <= 7;
        const isWarning = daysUntilExpiry <= 14;
        
        return (
          <div style={{
            background: isUrgent ? '#f8d7da' : (isWarning ? '#fff3cd' : '#fff3cd'),
            border: `2px solid ${isUrgent ? '#dc3545' : (isWarning ? '#fd7e14' : '#ffc107')}`,
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{ fontSize: '2rem' }}>
              {isUrgent ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#dc3545' }}>
                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#ffc107' }}>
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                  <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                </svg>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ 
                margin: '0 0 0.5rem 0', 
                color: isUrgent ? '#721c24' : '#856404',
                fontSize: '1.1rem'
              }}>
                {isUrgent ? 'URGENT: ' : ''}Client Secret Expiring Soon
              </h4>
              <p style={{ 
                margin: '0', 
                color: isUrgent ? '#721c24' : '#856404',
                fontSize: '0.95rem'
              }}>
                Your client secret will expire in <strong>{daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</strong> on{' '}
                <strong>{formatExpiryDate(settings.secretExpirationDate)}</strong>.
                {isUrgent && ' Your embedded reports will stop working after this date!'}
              </p>
            </div>
            <button
              onClick={regenerateSecret}
              className="btn btn-primary"
              disabled={loading || isSuspended}
              title={isSuspended ? 'Disabled - Subscription Suspended' : 'Regenerate Secret Now'}
            >
              Regenerate Now
            </button>
          </div>
        );
      })()}

      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          minWidth: '300px',
          maxWidth: '500px',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          background: toast.type === 'success' ? '#d1f2eb' : toast.type === 'error' ? '#f8d7da' : '#d1ecf1',
          color: toast.type === 'success' ? '#0c5460' : toast.type === 'error' ? '#721c24' : '#0c5460',
          border: `1px solid ${toast.type === 'success' ? '#7dcea0' : toast.type === 'error' ? '#f5c6cb' : '#bee5eb'}`,
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
              {toast.type === 'success' ? '[OK]' : toast.type === 'error' ? '[ERROR]' : '[INFO]'}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button 
              onClick={() => setToast({ show: false, message: '', type: '' })}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                padding: '0',
                lineHeight: '1',
                color: 'inherit',
                fontWeight: 'bold'
              }}
              title="Close"
            >
              X
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Section 1: Enable/Disable */}
      <div className="section" style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px', background: '#f8f9fa' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h3 style={{ margin: 0, color: '#495057', lineHeight: '1' }}>Status</h3>
          {hasAppRegistration && (
            <label className="switch" title={isSuspended ? 'Disabled - Subscription Suspended' : ''}>
              <input
                type="checkbox"
                checked={settings?.isEnabled ?? false}
                onChange={toggleEnabled}
                disabled={loading || isSuspended}
              />
              <span className="slider round"></span>
            </label>
          )}
          <span style={{ fontWeight: 'bold', color: settings?.isEnabled ? '#28a745' : '#6c757d', lineHeight: '1' }}>
            {settings?.isEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      {/* Section 2: App Registration */}
      <div className="section" style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px', background: '#f8f9fa' }}>
        <h3 style={{ color: '#495057' }}>Entra ID App Registration</h3>
        
        {/* Show loading state while initially fetching settings */}
        {loading && settings === null && !hasAppRegistration ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
            <div 
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }}
            />
            <p>Loading embedding configuration...</p>
          </div>
        ) : !hasAppRegistration ? (
          <>
            <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
              <strong>Warning:</strong> No app registration exists yet. Create one to enable OAuth2 authentication for embedding.
            </p>
            <button
              className="btn btn-primary"
              onClick={createAppRegistration}
              disabled={loading}
              style={{ background: '#1B394B', border: '1px solid #1B394B', color: 'white' }}
            >
              {loading ? 'Creating...' : 'Create App Registration'}
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ marginBottom: '0.5rem', color: '#495057' }}>
                <strong>Status:</strong> <span style={{ color: '#28a745' }}>[Active]</span> App Registered
              </p>
              <p style={{ marginBottom: '0.5rem', color: '#495057' }}>
                <strong>Display Name:</strong> {settings.entraAppDisplayName}
              </p>
            </div>

            <div style={{ marginBottom: '1rem', padding: '1rem', background: 'white', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <strong style={{ color: '#495057', minWidth: '180px' }}>Application (Client) ID:</strong>
                <code style={{ flex: 1, padding: '0.5rem', background: '#f1f3f5', borderRadius: '4px', color: '#495057', fontFamily: 'Consolas, Monaco, monospace' }}>
                  {settings.entraAppClientId}
                </code>
                <button
                  className="btn btn-primary"
                  onClick={() => copyToClipboard(settings.entraAppClientId)}
                  style={{ padding: '0.5rem 1rem', fontSize: '1rem', minHeight: '38px', minWidth: '70px', background: '#3A6A92', color: 'white' }}
                >
                  Copy
                </button>
              </div>

              {settings?.secretExpirationDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <strong style={{ color: '#495057', minWidth: '180px' }}>Secret Expiration Date:</strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                    <span style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: getExpiryStatusColor(daysUntilExpiry)
                    }}>
                      {formatExpiryDate(settings.secretExpirationDate)}
                    </span>
                    {daysUntilExpiry !== null && (
                      <span style={{
                        fontSize: '0.85rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: daysUntilExpiry <= 30 ? '#fff3cd' : '#e8f5e9',
                        color: daysUntilExpiry <= 30 ? '#856404' : '#2e7d32',
                        fontWeight: '600'
                      }}>
                        {daysUntilExpiry <= 0 ? 'EXPIRED' : `${daysUntilExpiry} days remaining`}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <strong style={{ color: '#495057', minWidth: '180px' }}>Client Secret:</strong>
                  {tempSecret ? (
                    <>
                      <code style={{ flex: 1, padding: '0.5rem', background: '#fff3cd', borderRadius: '4px', color: '#856404', fontFamily: 'Consolas, Monaco, monospace' }}>
                        {showSecret ? tempSecret : '*'.repeat(20) + tempSecret.slice(-8)}
                      </code>
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowSecret(!showSecret)}
                        style={{ padding: '0.5rem 1rem', fontSize: '1rem', minHeight: '38px', minWidth: '70px', background: '#3A6A92', color: 'white' }}
                      >
                        {showSecret ? 'Hide' : 'Show'}
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => copyToClipboard(tempSecret)}
                        style={{ padding: '0.5rem 1rem', fontSize: '1rem', minHeight: '38px', minWidth: '70px', background: '#3A6A92', color: 'white' }}
                      >
                        Copy
                      </button>
                    </>
                  ) : (
                    <>
                      <code style={{ flex: 1, padding: '0.5rem', background: '#f1f3f5', borderRadius: '4px', color: '#6c757d', fontFamily: 'Consolas, Monaco, monospace' }}>
                        ******************** (hidden)
                      </code>
                      <button
                        className="btn btn-primary"
                        onClick={regenerateSecret}
                        disabled={loading || isSuspended}
                        style={{ padding: '0.5rem 1rem', fontSize: '1rem', minHeight: '38px', minWidth: '100px', background: '#3A6A92', color: 'white' }}
                        title={isSuspended ? 'Disabled - Subscription Suspended' : 'Regenerate Client Secret'}
                      >
                        Regenerate
                      </button>
                    </>
                  )}
                </div>
                {!tempSecret && (
                  <p style={{ marginTop: '0.5rem', marginLeft: '195px', fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>
                    Note: We do not store a readable copy of the client secret. You must regenerate it to view a new secret.
                  </p>
                )}
              </div>

              {tempSecret && (
                <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                  <strong style={{ color: '#856404' }}>Important:</strong> <span style={{ color: '#856404' }}>Save this client secret securely now. It will only be shown once!</span>
                </div>
              )}
            </div>

            <button
              className="btn btn-danger"
              onClick={deleteAppRegistration}
              disabled={loading || isSuspended}
              title={isSuspended ? 'Disabled - Subscription Suspended' : 'Delete App Registration'}
            >
              Delete App Registration
            </button>
          </>
        )}
      </div>

      {/* Section 3: CORS Configuration */}
      {hasAppRegistration && (
        <div className="section" style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px', background: '#f8f9fa' }}>
          <h3 style={{ color: '#495057' }}>CORS Configuration</h3>
          <p style={{ color: '#6c757d', marginBottom: '1rem', paddingBottom: '0.25rem' }}>Add the domains that are allowed to call the embed token API.</p>

          <div style={{ marginBottom: '1rem' }}>
            {allowedOrigins.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {allowedOrigins.map((origin, index) => (
                  <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <div style={{ flex: 1, padding: '0.75rem', background: 'white', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                      <span style={{ color: '#495057', fontSize: '1rem', fontFamily: 'inherit' }}>{origin}</span>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => removeOrigin(origin)}
                      disabled={loading || isSuspended}
                      style={{ padding: '0.5rem 1rem', fontSize: '1rem', minHeight: '38px', minWidth: 'auto', whiteSpace: 'nowrap', background: '#3A6A92', color: 'white' }}
                      title={isSuspended ? 'Disabled - Subscription Suspended' : 'Remove Origin'}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#6c757d', fontStyle: 'italic' }}>No origins configured yet. Add domains that will embed your reports.</p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              className="form-control"
              placeholder="https://app.customer.com"
              value={newOrigin}
              onChange={(e) => setNewOrigin(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isSuspended && addOrigin()}
              disabled={loading || isSuspended}
              style={{ fontSize: '1rem', height: '38px', flex: 1 }}
            />
            <button
              className="btn btn-primary"
              onClick={addOrigin}
              disabled={loading || isSuspended}
              style={{ padding: '0.5rem 1rem', fontSize: '1rem', minHeight: '38px', whiteSpace: 'nowrap', background: '#3A6A92', color: 'white' }}
              title={isSuspended ? 'Disabled - Subscription Suspended' : 'Add CORS Origin'}
            >
              Add URL
            </button>
          </div>
        </div>
      )}

      {/* Section 4: API Connection Details */}
      {hasAppRegistration && (
        <div className="section" style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px', background: '#f8f9fa' }}>
          <h3 style={{ color: '#495057' }}>API Connection Details</h3>
          
          <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
            <p style={{ marginBottom: '0.5rem', color: '#495057' }}><strong>API Endpoint:</strong></p>
            <code style={{ display: 'block', padding: '0.75rem', background: 'white', borderRadius: '4px', marginBottom: '1rem', color: '#495057', border: '1px solid #dee2e6', fontFamily: 'Consolas, Monaco, monospace' }}>
              {EMBED_API_URL}/embed-token
            </code>

            <p style={{ marginBottom: '0.5rem', color: '#495057' }}><strong>Tenant ID:</strong></p>
            <code style={{ display: 'block', padding: '0.75rem', background: 'white', borderRadius: '4px', marginBottom: '1rem', color: '#495057', border: '1px solid #dee2e6', fontFamily: 'Consolas, Monaco, monospace' }}>
              {settings?.entraAppTenantId || 'Not configured'}
            </code>

            <p style={{ marginBottom: '0.5rem', color: '#495057' }}><strong>API Scope:</strong></p>
            <code style={{ display: 'block', padding: '0.75rem', background: 'white', borderRadius: '4px', marginBottom: '1rem', color: '#495057', border: '1px solid #dee2e6', fontFamily: 'Consolas, Monaco, monospace' }}>
              {settings?.entraAppAudience ? `${settings.entraAppAudience}/.default` : 'Not configured'}
            </code>

            <p style={{ marginBottom: '0.5rem', color: '#495057' }}><strong>Token Endpoint:</strong></p>
            <code style={{ display: 'block', padding: '0.75rem', background: 'white', borderRadius: '4px', color: '#495057', border: '1px solid #dee2e6', fontFamily: 'Consolas, Monaco, monospace', wordBreak: 'break-all' }}>
              https://login.microsoftonline.com/{settings?.entraAppTenantId || 'your-tenant-id'}/oauth2/v2.0/token
            </code>
          </div>
        </div>
      )}

      {/* Section 5: Code Examples */}
      {hasAppRegistration && (
        <div className="section" style={{ marginBottom: '2rem' }}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowCodeExamples(!showCodeExamples)}
            style={{ background: '#1B394B', border: '1px solid #1B394B', color: 'white' }}
          >
            {showCodeExamples ? 'Hide' : 'Show'} Integration Code Examples
          </button>

          {showCodeExamples && (
            <CodeExamples
              clientId={settings.entraAppClientId}
              tenantId={settings.entraAppTenantId}
              apiScope={settings.entraAppAudience}
              apiEndpoint={`${EMBED_API_URL}/embed-token`}
            />
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, color: '#dc3545' }}>Delete App Registration</h3>
            <p style={{ color: '#495057', marginBottom: '1rem' }}>
              Are you sure you want to delete the app registration?
            </p>
            <p style={{ color: '#721c24', marginBottom: '1.5rem', padding: '1rem', background: '#f8d7da', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
              <strong>Warning:</strong> This will invalidate all existing authentication and cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Secret Confirmation Modal */}
      {showRegenerateConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, color: '#ffc107' }}>Regenerate Client Secret</h3>
            <p style={{ color: '#495057', marginBottom: '1rem' }}>
              Are you sure you want to regenerate the client secret?
            </p>
            <p style={{ color: '#856404', marginBottom: '1.5rem', padding: '1rem', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
              <strong>Warning:</strong> The old secret will be immediately invalidated and any applications using it will stop working.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowRegenerateConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-warning"
                onClick={handleConfirmRegenerate}
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Modal for Long-Running Operations */}
      {loading && loadingOperation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10002,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2.5rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            {/* Spinner */}
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1.5rem'
            }}></div>
            
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>
              {loadingOperation === 'create' ? 'Creating App Registration' : 
               loadingOperation === 'delete' ? 'Deleting App Registration' : 
               loadingOperation === 'regenerate' ? 'Regenerating Secret' : 'Processing...'}
            </h3>
            <p style={{ margin: 0, color: '#6c757d', fontSize: '0.95rem' }}>
              This may take 10-15 seconds...
            </p>
            <p style={{ margin: '1rem 0 0 0', color: '#6c757d', fontSize: '0.85rem', fontStyle: 'italic' }}>
              {loadingOperation === 'create' ? 'Please wait while we create your OAuth2 credentials' : 'Please wait while we process your request'}
            </p>
          </div>
        </div>
      )}
      
      {/* Add keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Code Examples Component
const CodeExamples = ({ clientId, tenantId, apiScope, apiEndpoint }) => {
  const [activeTab, setActiveTab] = useState('csharp');

  // Ensure API scope has /.default suffix and no api:// prefix
  // Database stores just the Client ID, we need to add /.default for the scope
  let fullApiScope = apiScope || 'YOUR-API-ID';
  
  // Remove api:// prefix if present
  if (fullApiScope.startsWith('api://')) {
    fullApiScope = fullApiScope.substring(6);
  }
  
  // Remove trailing /.default if present (we'll add it back)
  if (fullApiScope.endsWith('/.default')) {
    fullApiScope = fullApiScope.substring(0, fullApiScope.length - 10);
  }
  
  // Now add /.default for the actual scope request
  fullApiScope = `${fullApiScope}/.default`;
  
  // Function to generate and download PDF guide
  const downloadPDFGuide = (language) => {
    const guides = {
      csharp: generateCSharpGuide(),
      nodejs: generateNodeJSGuide(),
      python: generatePythonGuide(),
      javascript: generateJavaScriptGuide()
    };
    
    const guide = guides[language];
    if (!guide) return;
    
    // Create a simple text-based guide that can be saved as PDF
    const content = guide.content;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PowerBI-Embed-Integration-Guide-${guide.title}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const generateCSharpGuide = () => ({
    title: 'CSharp-DotNet',
    content: `
================================================================================
                    Power BI Embedding Integration Guide
                          .NET / C# Implementation
================================================================================

OVERVIEW
--------
This guide provides step-by-step instructions for embedding Power BI reports
in your .NET application using OAuth2 authentication with Azure Entra ID.

PREREQUISITES
-------------
* .NET 8.0 SDK or higher
* Azure Entra ID App Registration (created via Management Portal)
* Client ID and Client Secret (obtained from Management Portal)
* Visual Studio 2022 or VS Code with C# extension


STEP 1: INSTALL REQUIRED NUGET PACKAGES
----------------------------------------
Run these commands in your project directory:

  dotnet add package Microsoft.Identity.Client
  dotnet add package System.Net.Http.Json

Or add to your .csproj file:

  <PackageReference Include="Microsoft.Identity.Client" Version="4.61.0" />
  <PackageReference Include="System.Net.Http.Json" Version="8.0.0" />


STEP 2: CONFIGURE APP SETTINGS
-------------------------------
Add the following to your appsettings.json:

{
  "PowerBIEmbed": {
    "TenantId": "${tenantId || 'YOUR_TENANT_ID'}",
    "ClientId": "${clientId || 'YOUR_CLIENT_ID'}",
    "ClientSecret": "YOUR_CLIENT_SECRET_FROM_MANAGEMENT_PORTAL",
    "ApiScope": "${fullApiScope}",
    "EmbedApiEndpoint": "${apiEndpoint}"
  }
}

>>> IMPORTANT: Store ClientSecret securely!
- Use Azure Key Vault in production
- Use User Secrets in development: dotnet user-secrets set "PowerBIEmbed:ClientSecret" "your-secret"
- NEVER commit secrets to source control


STEP 3: CREATE THE EMBED SERVICE
---------------------------------
Create a new file: Services/PowerBIEmbedService.cs

using Microsoft.Identity.Client;
using System.Net.Http.Headers;

namespace YourApp.Services
{
    public class PowerBIEmbedService
    {
        private readonly IConfiguration _configuration;
        private readonly IHttpClientFactory _httpClientFactory;
        private readonly ILogger<PowerBIEmbedService> _logger;

        public PowerBIEmbedService(
            IConfiguration configuration,
            IHttpClientFactory httpClientFactory,
            ILogger<PowerBIEmbedService> logger)
        {
            _configuration = configuration;
            _httpClientFactory = httpClientFactory;
            _logger = logger;
        }

        public async Task<EmbedTokenResponse> GetEmbedTokenAsync(
            string userEmail,
            Guid reportId,
            Guid workspaceId)
        {
            var config = _configuration.GetSection("PowerBIEmbed");
            var tenantId = config["TenantId"] 
                ?? throw new InvalidOperationException("TenantId is required");
            var clientId = config["ClientId"] 
                ?? throw new InvalidOperationException("ClientId is required");
            var clientSecret = config["ClientSecret"] 
                ?? throw new InvalidOperationException("ClientSecret is required");
            var apiScope = config["ApiScope"] 
                ?? throw new InvalidOperationException("ApiScope is required");
            var embedApiEndpoint = config["EmbedApiEndpoint"] 
                ?? throw new InvalidOperationException("EmbedApiEndpoint is required");

            // Step 1: Get access token from Entra ID
            _logger.LogInformation("Acquiring token for embed API");
            
            var app = ConfidentialClientApplicationBuilder
                .Create(clientId)
                .WithClientSecret(clientSecret)
                .WithAuthority(new Uri($"https://login.microsoftonline.com/{tenantId}"))
                .Build();

            var authResult = await app.AcquireTokenForClient(new[] { apiScope })
                .ExecuteAsync();

            // Step 2: Call embed token API
            // Note: No subscriptionId needed - it's mapped from your client ID
            var httpClient = _httpClientFactory.CreateClient();
            httpClient.DefaultRequestHeaders.Authorization =
                new AuthenticationHeaderValue("Bearer", authResult.AccessToken);

            var request = new { userEmail, reportId, workspaceId };
            
            _logger.LogInformation(
                "Requesting embed token for user {UserEmail}, report {ReportId}",
                userEmail, reportId);

            var response = await httpClient.PostAsJsonAsync(
                embedApiEndpoint, request);

            if (!response.IsSuccessStatusCode)
            {
                var error = await response.Content.ReadAsStringAsync();
                throw new HttpRequestException(
                    $"Failed to get embed token: {response.StatusCode} - {error}");
            }
            
            var embedToken = await response.Content
                .ReadFromJsonAsync<EmbedTokenResponse>();
            
            if (embedToken == null)
            {
                throw new InvalidOperationException(
                    "Failed to deserialize embed token response");
            }
            
            _logger.LogInformation("Embed token received successfully");
            
            return embedToken;
        }
    }

    public class EmbedTokenResponse
    {
        public string EmbedToken { get; set; } = string.Empty;
        public string EmbedUrl { get; set; } = string.Empty;
        public string ReportId { get; set; } = string.Empty;
        public string ReportName { get; set; } = string.Empty;
        public DateTime TokenExpiration { get; set; }
    }
}


STEP 4: REGISTER THE SERVICE
-----------------------------
In your Program.cs, add:

// Register HttpClientFactory
builder.Services.AddHttpClient();

// Register PowerBIEmbedService
builder.Services.AddScoped<PowerBIEmbedService>();


STEP 5: CREATE THE RAZOR PAGE (for ASP.NET Core)
-------------------------------------------------
Create Pages/PowerBIEmbed.cshtml.cs:

using Microsoft.AspNetCore.Mvc;
using Microsoft.AspNetCore.Mvc.RazorPages;
using YourApp.Services;

namespace YourApp.Pages
{
    public class PowerBIEmbedModel : PageModel
    {
        private readonly PowerBIEmbedService _embedService;
        private readonly ILogger<PowerBIEmbedModel> _logger;

        [BindProperty]
        public Guid WorkspaceId { get; set; }

        [BindProperty]
        public Guid ReportId { get; set; }

        public string? UserEmail { get; set; }
        public string? EmbedToken { get; set; }
        public string? EmbedUrl { get; set; }
        public string? ActualReportId { get; set; }
        public string? ErrorMessage { get; set; }

        public PowerBIEmbedModel(
            PowerBIEmbedService embedService,
            ILogger<PowerBIEmbedModel> logger)
        {
            _embedService = embedService;
            _logger = logger;
        }

        public void OnGet()
        {
            // Get user email from authenticated user
            UserEmail = User.Identity?.Name ?? "unknown@example.com";
        }

        public async Task<IActionResult> OnPostAsync()
        {
            UserEmail = User.Identity?.Name ?? "unknown@example.com";

            try
            {
                var embedData = await _embedService.GetEmbedTokenAsync(
                    UserEmail,
                    ReportId,
                    WorkspaceId);

                EmbedToken = embedData.EmbedToken;
                EmbedUrl = embedData.EmbedUrl;
                ActualReportId = embedData.ReportId;
            }
            catch (Exception ex)
            {
                _logger.LogError(ex, "Error getting embed token");
                ErrorMessage = ex.Message;
            }

            return Page();
        }
    }
}


STEP 6: CREATE THE VIEW
------------------------
Create Pages/PowerBIEmbed.cshtml:

@page
@model PowerBIEmbedModel

<div class="container mt-4">
    <h1>Power BI Embedded Report</h1>

    <form method="post" class="mb-4">
        <div class="mb-3">
            <label>Workspace ID</label>
            <input type="text" class="form-control" asp-for="WorkspaceId" 
                   placeholder="12345678-1234-1234-1234-123456789abc" required />
        </div>

        <div class="mb-3">
            <label>Report ID</label>
            <input type="text" class="form-control" asp-for="ReportId" 
                   placeholder="87654321-4321-4321-4321-cba987654321" required />
        </div>

        <button type="submit" class="btn btn-primary">Load Report</button>
    </form>

    @if (!string.IsNullOrEmpty(Model.ErrorMessage))
    {
        <div class="alert alert-danger">@Model.ErrorMessage</div>
    }

    @if (!string.IsNullOrEmpty(Model.EmbedToken))
    {
        <div id="embedContainer" style="height: 600px; border: 1px solid #ddd;">
        </div>
    }
</div>

@section Scripts {
    @if (!string.IsNullOrEmpty(Model.EmbedToken))
    {
        <script src="https://cdn.jsdelivr.net/npm/powerbi-client@2.23.0/dist/powerbi.min.js">
        </script>
        <script>
            const models = window['powerbi-client'].models;
            const embedContainer = document.getElementById('embedContainer');
            
            const config = {
                type: 'report',
                tokenType: models.TokenType.Embed,
                accessToken: '@Html.Raw(Model.EmbedToken)',
                embedUrl: '@Html.Raw(Model.EmbedUrl)',
                id: '@Model.ActualReportId',
                permissions: models.Permissions.Read,
                settings: {
                    panes: {
                        filters: { visible: true },
                        pageNavigation: { visible: true }
                    }
                }
            };
            
            const powerbi = window.powerbi;
            const report = powerbi.embed(embedContainer, config);
            
            report.on("loaded", () => console.log("Report loaded"));
            report.on("error", (event) => {
                console.error("Error:", event.detail);
                alert("Error loading report: " + event.detail.message);
            });
        </script>
    }
}


CONFIGURATION CHECKLIST
-----------------------
Before running your application, verify:

* NuGet packages installed
* appsettings.json configured with all values
* Client Secret stored securely (Key Vault or User Secrets)
* PowerBIEmbedService registered in Program.cs
* HttpClientFactory registered in Program.cs
* CORS origin added in Management Portal (if calling from browser)
* User email exists and has license in MerlinIQ system
* Report is assigned to the user in MerlinIQ system


FINDING WORKSPACE AND REPORT IDs
---------------------------------
1. Log into Management Portal
2. Navigate to Reports tab
3. Click the "Embed" button next to the report you want to embed
4. Copy the Workspace ID and Report ID from the popup


TROUBLESHOOTING
---------------
ERROR: "App not registered for embedding"
  - Verify your Client ID matches the one in Management Portal
  - Check that embedding is enabled in the Customer Embedding tab

ERROR: "User not authorized"
  - Verify the user email exists in the Users tab
  - Check that the user has an active license
  - Ensure the report is assigned to the user

ERROR: "Invalid token" or "Authentication failed"
  - Verify TenantId, ClientId, and ClientSecret are correct
  - Check that ApiScope matches the Management Portal value
  - Ensure Client Secret hasn't expired (regenerate if needed)

ERROR: "CORS error" in browser
  - Add your application's origin to CORS Configuration in Management Portal
  - Example: https://yourapp.azurewebsites.net


SECURITY BEST PRACTICES
------------------------
? Store Client Secret in Azure Key Vault (production)
? Use User Secrets for development (dotnet user-secrets)
? NEVER expose Client Secret in browser/client-side code
? Always call embed API from your backend server
? Regenerate Client Secret regularly (every 6-12 months)
? Use HTTPS for all production deployments
? Implement proper error handling and logging


NEXT STEPS
----------
1. Test with a single report first
2. Implement error handling and user feedback
3. Add caching for embed tokens (valid for ~1 hour)
4. Implement token refresh logic before expiration
5. Add report selection UI for end users
6. Monitor API usage and performance


SUPPORT
-------
For additional help:
- Review Management Portal Help tab
- Check Azure Function logs for detailed errors
- Contact your MerlinIQ administrator


???????????????????????????????????????????????????????????????????????????????
Generated by MerlinIQ Management Portal - Customer Embedding Configuration
???????????????????????????????????????????????????????????????????????????????
`
  });
  
  const generateNodeJSGuide = () => ({
    title: 'NodeJS',
    content: `
================================================================================
                    Power BI Embedding Integration Guide
                            Node.js Implementation
================================================================================

OVERVIEW
--------
This guide provides step-by-step instructions for embedding Power BI reports
in your Node.js application using OAuth2 authentication with Azure Entra ID.

PREREQUISITES
-------------
* Node.js 18.x or higher
* npm or yarn package manager
* Azure Entra ID App Registration (created via Management Portal)
* Client ID and Client Secret (obtained from Management Portal)


STEP 1: INSTALL REQUIRED PACKAGES
----------------------------------
Run these commands in your project directory:

  npm install @azure/msal-node axios express

Or using yarn:

  yarn add @azure/msal-node axios express


STEP 2: CREATE ENVIRONMENT CONFIGURATION
-----------------------------------------
Create a .env file in your project root:

POWERBI_TENANT_ID=${tenantId || 'YOUR_TENANT_ID'}
POWERBI_CLIENT_ID=${clientId || 'YOUR_CLIENT_ID'}
POWERBI_CLIENT_SECRET=YOUR_CLIENT_SECRET_FROM_MANAGEMENT_PORTAL
POWERBI_API_SCOPE=${fullApiScope}
POWERBI_EMBED_API_ENDPOINT=${apiEndpoint}
PORT=3000

>>> IMPORTANT: Protect your .env file!
   - Add .env to your .gitignore file
   - Use Azure Key Vault or AWS Secrets Manager in production
   - NEVER commit secrets to source control

Also install dotenv package:

  npm install dotenv


STEP 3: CREATE THE EMBED SERVICE
---------------------------------
Create a new file: services/powerBIEmbedService.js

const msal = require('@azure/msal-node');
const axios = require('axios');

class PowerBIEmbedService {
    constructor() {
        this.msalConfig = {
            auth: {
                clientId: process.env.POWERBI_CLIENT_ID,
                authority: \`https://login.microsoftonline.com/\${process.env.POWERBI_TENANT_ID}\`,
                clientSecret: process.env.POWERBI_CLIENT_SECRET
            }
        };
        
        this.cca = new msal.ConfidentialClientApplication(this.msalConfig);
        this.apiScope = process.env.POWERBI_API_SCOPE;
        this.embedApiEndpoint = process.env.POWERBI_EMBED_API_ENDPOINT;
    }

    async getEmbedToken(userEmail, reportId, workspaceId) {
        try {
            console.log(\`Getting embed token for user: \${userEmail}\`);

            // Step 1: Get access token from Entra ID
            const tokenRequest = {
                scopes: [this.apiScope]
            };
            
            const authResponse = await this.cca.acquireTokenByClientCredential(
                tokenRequest
            );
            
            if (!authResponse || !authResponse.accessToken) {
                throw new Error('Failed to acquire access token from Entra ID');
            }

            console.log('Access token acquired successfully');

            // Step 2: Call embed token API
            // Note: No subscriptionId needed - it's mapped from your client ID
            const requestBody = {
                userEmail: userEmail,
                reportId: reportId,
                workspaceId: workspaceId
            };

            console.log(\`Requesting embed token for report: \${reportId}\`);

            const response = await axios.post(
                this.embedApiEndpoint,
                requestBody,
                {
                    headers: {
                        'Authorization': \`Bearer \${authResponse.accessToken}\`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.data) {
                throw new Error('Empty response from embed API');
            }

            console.log('Embed token received successfully');

            return {
                embedToken: response.data.embedToken,
                embedUrl: response.data.embedUrl,
                reportId: response.data.reportId,
                reportName: response.data.reportName,
                tokenExpiration: response.data.tokenExpiration
            };

        } catch (error) {
            console.error('Error getting embed token:', error.message);
            
            if (error.response) {
                // API returned an error response
                console.error('API Error Status:', error.response.status);
                console.error('API Error Data:', error.response.data);
                throw new Error(
                    \`Embed API error: \${error.response.status} - \${JSON.stringify(error.response.data)}\`
                );
            } else if (error.request) {
                // Request was made but no response received
                console.error('No response from API');
                throw new Error('No response from embed API - check network and endpoint URL');
            } else {
                // Something else went wrong
                throw error;
            }
        }
    }
}

module.exports = PowerBIEmbedService;


STEP 4: CREATE EXPRESS SERVER
------------------------------
Create a new file: server.js

require('dotenv').config();
const express = require('express');
const PowerBIEmbedService = require('./services/powerBIEmbedService');

const app = express();
const embedService = new PowerBIEmbedService();

// Middleware
app.use(express.json());
app.use(express.static('public'));

// API endpoint to get embed token
app.post('/api/embed-token', async (req, res) => {
    try {
        const { userEmail, reportId, workspaceId } = req.body;

        // Validate required parameters
        if (!userEmail) {
            return res.status(400).json({ error: 'userEmail is required' });
        }
        if (!reportId) {
            return res.status(400).json({ error: 'reportId is required' });
        }
        if (!workspaceId) {
            return res.status(400).json({ error: 'workspaceId is required' });
        }

        console.log(\`Embed token request: User=\${userEmail}, Report=\${reportId}\`);

        const embedData = await embedService.getEmbedToken(
            userEmail,
            reportId,
            workspaceId
        );

        res.json(embedData);

    } catch (error) {
        console.error('Error in /api/embed-token:', error);
        res.status(500).json({ 
            error: 'Failed to get embed token',
            message: error.message 
        });
    }
});

// Health check endpoint
app.get('/health', (req, res) => {
    res.json({ status: 'ok', timestamp: new Date().toISOString() });
});

const PORT = process.env.PORT || 3000;
app.listen(PORT, () => {
    console.log(\`Server running on port \${PORT}\`);
    console.log(\`Environment: \${process.env.NODE_ENV || 'development'}\`);
});


STEP 5: CREATE THE FRONTEND HTML
---------------------------------
Create a new file: public/index.html

<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Power BI Embed</title>
    <script src="https://cdn.jsdelivr.net/npm/powerbi-client@2.23.0/dist/powerbi.min.js">
    </script>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 20px;
            background-color: #f5f5f5;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .form-group {
            margin-bottom: 15px;
        }
        label {
            display: block;
            margin-bottom: 5px;
            font-weight: bold;
        }
        input {
            width: 100%;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            box-sizing: border-box;
        }
        button {
            background-color: #0078d4;
            color: white;
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
        }
        button:hover {
            background-color: #005a9e;
        }
        button:disabled {
            background-color: #ccc;
            cursor: not-allowed;
        }
        #embedContainer {
            margin-top: 20px;
            height: 600px;
            border: 1px solid #ddd;
            border-radius: 4px;
        }
        .error {
            color: #d13438;
            background-color: #fef0f0;
            padding: 10px;
            border-radius: 4px;
            margin-top: 10px;
        }
        .loading {
            text-align: center;
            padding: 20px;
            color: #666;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>Power BI Embedded Report</h1>
        
        <div class="form-group">
            <label for="userEmail">User Email:</label>
            <input type="email" id="userEmail" 
                   placeholder="user@example.com" required>
        </div>

        <div class="form-group">
            <label for="workspaceId">Workspace ID:</label>
            <input type="text" id="workspaceId" 
                   placeholder="12345678-1234-1234-1234-123456789abc" required>
        </div>

        <div class="form-group">
            <label for="reportId">Report ID:</label>
            <input type="text" id="reportId" 
                   placeholder="87654321-4321-4321-4321-cba987654321" required>
        </div>

        <button id="loadButton" onclick="loadReport()">Load Report</button>

        <div id="error" class="error" style="display:none;"></div>
        <div id="loading" class="loading" style="display:none;">
            Loading report...
        </div>
        <div id="embedContainer" style="display:none;"></div>
    </div>

    <script>
        async function loadReport() {
            const userEmail = document.getElementById('userEmail').value;
            const workspaceId = document.getElementById('workspaceId').value;
            const reportId = document.getElementById('reportId').value;
            const loadButton = document.getElementById('loadButton');
            const errorDiv = document.getElementById('error');
            const loadingDiv = document.getElementById('loading');
            const embedContainer = document.getElementById('embedContainer');

            // Validate inputs
            if (!userEmail || !workspaceId || !reportId) {
                showError('Please fill in all fields');
                return;
            }

            // Show loading, hide error
            errorDiv.style.display = 'none';
            loadingDiv.style.display = 'block';
            embedContainer.style.display = 'none';
            loadButton.disabled = true;

            try {
                console.log('Requesting embed token...');

                // Call backend API to get embed token
                const response = await fetch('/api/embed-token', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        userEmail: userEmail,
                        reportId: reportId,
                        workspaceId: workspaceId
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || \`HTTP \${response.status}\`);
                }

                const embedData = await response.json();
                console.log('Embed token received');

                // Hide loading, show container
                loadingDiv.style.display = 'none';
                embedContainer.style.display = 'block';

                // Embed the report
                const models = window['powerbi-client'].models;
                const config = {
                    type: 'report',
                    tokenType: models.TokenType.Embed,
                    accessToken: embedData.embedToken,
                    embedUrl: embedData.embedUrl,
                    id: embedData.reportId,
                    permissions: models.Permissions.Read,
                    settings: {
                        panes: {
                            filters: { visible: true },
                            pageNavigation: { visible: true }
                        }
                    }
                };

                const powerbi = window.powerbi;
                const report = powerbi.embed(embedContainer, config);

                report.on('loaded', () => {
                    console.log('Report loaded successfully');
                });

                report.on('rendered', () => {
                    console.log('Report rendered successfully');
                });

                report.on('error', (event) => {
                    console.error('Report error:', event.detail);
                    showError(\`Report error: \${event.detail.message}\`);
                });

            } catch (error) {
                console.error('Error loading report:', error);
                showError(\`Failed to load report: \${error.message}\`);
                loadingDiv.style.display = 'none';
            } finally {
                loadButton.disabled = false;
            }
        }

        function showError(message) {
            const errorDiv = document.getElementById('error');
            errorDiv.textContent = message;
            errorDiv.style.display = 'block';
        }
    </script>
</body>
</html>


STEP 6: ADD START SCRIPT TO PACKAGE.JSON
-----------------------------------------
Add to your package.json:

{
  "name": "powerbi-embed-app",
  "version": "1.0.0",
  "scripts": {
    "start": "node server.js",
    "dev": "nodemon server.js"
  },
  "dependencies": {
    "@azure/msal-node": "^2.6.0",
    "axios": "^1.6.0",
    "dotenv": "^16.3.0",
    "express": "^4.18.0"
  },
  "devDependencies": {
    "nodemon": "^3.0.0"
  }
}


STEP 7: RUN YOUR APPLICATION
-----------------------------
Start the server:

  npm start

Or with auto-reload during development:

  npm install --save-dev nodemon
  npm run dev

Open browser to: http://localhost:3000


CONFIGURATION CHECKLIST
-----------------------
Before running your application, verify:

* All npm packages installed
* .env file created with all required values
* .env file added to .gitignore
* Client Secret stored securely (not in source control)
* CORS origin added in Management Portal (http://localhost:3000)
* User email exists and has license in MerlinIQ system
* Report is assigned to the user in MerlinIQ system


FINDING WORKSPACE AND REPORT IDs
---------------------------------
1. Log into Management Portal
2. Navigate to Reports tab
3. Click the "Embed" button next to the report you want to embed
4. Copy the Workspace ID and Report ID from the popup


TROUBLESHOOTING
---------------
ERROR: "App not registered for embedding"
  - Verify your Client ID matches the one in Management Portal
  - Check that embedding is enabled in the Customer Embedding tab

ERROR: "User not authorized"
  - Verify the user email exists in the Users tab
  - Check that the user has an active license
  - Ensure the report is assigned to the user

ERROR: "Failed to acquire access token"
  - Verify POWERBI_TENANT_ID, CLIENT_ID, and CLIENT_SECRET are correct
  - Check that API_SCOPE matches the Management Portal value
  - Ensure Client Secret hasn't expired (regenerate if needed)

ERROR: "CORS error" in browser
  - Add http://localhost:3000 to CORS Configuration in Management Portal
  - For production, add your actual domain

ERROR: ECONNREFUSED
  - Check that EMBED_API_ENDPOINT is correct and accessible
  - Verify your network allows outbound HTTPS connections


SECURITY BEST PRACTICES
------------------------
* Store secrets in environment variables (.env file)
* Add .env to .gitignore
* Use Azure Key Vault or AWS Secrets Manager in production
* NEVER expose Client Secret in browser/client-side code
* Always call embed API from your backend server
* Implement rate limiting on your API endpoints
* Use HTTPS in production (get SSL certificate)
* Implement proper error handling and logging
* Regenerate Client Secret regularly (every 6-12 months)


NEXT STEPS
----------
1. Test with a single report first
2. Add user authentication to your application
3. Implement token caching (embed tokens valid for ~1 hour)
4. Add token refresh logic before expiration
5. Create a report selection UI
6. Deploy to production (Azure App Service, AWS, etc.)
7. Monitor API usage and performance


DEPLOYMENT OPTIONS
------------------
Azure App Service:
  - Use App Settings for environment variables
  - Enable HTTPS/SSL
  - Configure custom domain

AWS Elastic Beanstalk:
  - Use Environment Properties for secrets
  - Configure HTTPS with ACM certificate

Docker:
  - Use secrets management (Docker Secrets, Kubernetes Secrets)
  - Never bake secrets into images


SUPPORT
-------
For additional help:
- Review Management Portal Help tab
- Check server console logs for detailed errors
- Contact your MerlinIQ administrator


???????????????????????????????????????????????????????????????????????????????
Generated by MerlinIQ Management Portal - Customer Embedding Configuration
???????????????????????????????????????????????????????????????????????????????
`
  });
  
  const generatePythonGuide = () => ({
    title: 'Python',
    content: `
================================================================================
                    Power BI Embedding Integration Guide
                             Python Implementation
================================================================================

OVERVIEW
--------
This guide provides step-by-step instructions for embedding Power BI reports
in your Python application using OAuth2 authentication with Azure Entra ID.

PREREQUISITES
-------------
* Python 3.8 or higher
* pip package manager
* Azure Entra ID App Registration (created via Management Portal)
* Client ID and Client Secret (obtained from Management Portal)


STEP 1: INSTALL REQUIRED PACKAGES
----------------------------------
Run these commands in your project directory:

  pip install msal requests flask python-dotenv

Or add to requirements.txt:

msal==1.26.0
requests==2.31.0
Flask==3.0.0
python-dotenv==1.0.0


STEP 2: CREATE ENVIRONMENT CONFIGURATION
-----------------------------------------
Create a .env file in your project root:

POWERBI_TENANT_ID=${tenantId || 'YOUR_TENANT_ID'}
POWERBI_CLIENT_ID=${clientId || 'YOUR_CLIENT_ID'}
POWERBI_CLIENT_SECRET=YOUR_CLIENT_SECRET_FROM_MANAGEMENT_PORTAL
POWERBI_API_SCOPE=${fullApiScope}
POWERBI_EMBED_API_ENDPOINT=${apiEndpoint}
FLASK_SECRET_KEY=your-random-secret-key-here

>>> IMPORTANT: Protect your .env file!
   - Add .env to your .gitignore file
   - Use Azure Key Vault or AWS Secrets Manager in production
   - NEVER commit secrets to source control


STEP 3: CREATE THE EMBED SERVICE
---------------------------------
Create a new file: services/powerbi_embed_service.py

from msal import ConfidentialClientApplication
import requests
import os
import logging

logger = logging.getLogger(__name__)

class PowerBIEmbedService:
    def __init__(self):
        self.tenant_id = os.getenv('POWERBI_TENANT_ID')
        self.client_id = os.getenv('POWERBI_CLIENT_ID')
        self.client_secret = os.getenv('POWERBI_CLIENT_SECRET')
        self.api_scope = os.getenv('POWERBI_API_SCOPE')
        self.embed_api_endpoint = os.getenv('POWERBI_EMBED_API_ENDPOINT')
        
        if not all([self.tenant_id, self.client_id, self.client_secret, 
                   self.api_scope, self.embed_api_endpoint]):
            raise ValueError('Missing required Power BI configuration')
        
        self.authority = f'https://login.microsoftonline.com/{self.tenant_id}'
        self.app = ConfidentialClientApplication(
            client_id=self.client_id,
            client_credential=self.client_secret,
            authority=self.authority
        )
    
    def get_embed_token(self, user_email: str, report_id: str, 
                       workspace_id: str) -> dict:
        """
        Get embed token for a Power BI report
        
        Args:
            user_email: Email of the user requesting access
            report_id: GUID of the Power BI report
            workspace_id: GUID of the Power BI workspace
            
        Returns:
            dict: Contains embedToken, embedUrl, reportId, reportName, 
                  tokenExpiration
            
        Raises:
            Exception: If authentication or API call fails
        """
        try:
            logger.info(f'Getting embed token for user: {user_email}')
            
            # Step 1: Get access token from Entra ID
            token_response = self.app.acquire_token_for_client(
                scopes=[self.api_scope]
            )
            
            if 'access_token' not in token_response:
                error_msg = token_response.get('error_description', 
                                              'Failed to acquire token')
                logger.error(f'Token acquisition failed: {error_msg}')
                raise Exception(f'Failed to acquire access token: {error_msg}')
            
            logger.info('Access token acquired successfully')
            
            # Step 2: Call embed token API
            # Note: No subscriptionId needed - it's mapped from your client ID
            headers = {
                'Authorization': f"Bearer {token_response['access_token']}",
                'Content-Type': 'application/json'
            }
            
            request_body = {
                'userEmail': user_email,
                'reportId': report_id,
                'workspaceId': workspace_id
            }
            
            logger.info(f'Requesting embed token for report: {report_id}')
            
            response = requests.post(
                self.embed_api_endpoint,
                json=request_body,
                headers=headers,
                timeout=30
            )
            
            response.raise_for_status()
            
            embed_data = response.json()
            logger.info('Embed token received successfully')
            
            return {
                'embedToken': embed_data.get('embedToken'),
                'embedUrl': embed_data.get('embedUrl'),
                'reportId': embed_data.get('reportId'),
                'reportName': embed_data.get('reportName'),
                'tokenExpiration': embed_data.get('tokenExpiration')
            }
            
        except requests.exceptions.HTTPError as e:
            logger.error(f'HTTP error: {e.response.status_code} - {e.response.text}')
            raise Exception(
                f'Embed API error: {e.response.status_code} - {e.response.text}'
            )
        except requests.exceptions.RequestException as e:
            logger.error(f'Request error: {str(e)}')
            raise Exception(f'Network error: {str(e)}')
        except Exception as e:
            logger.error(f'Unexpected error: {str(e)}')
            raise


STEP 4: CREATE FLASK APPLICATION
---------------------------------
Create a new file: app.py

from flask import Flask, render_template, request, jsonify
from services.powerbi_embed_service import PowerBIEmbedService
from dotenv import load_dotenv
import os
import logging

# Load environment variables
load_dotenv()

# Configure logging
logging.basicConfig(
    level=logging.INFO,
    format='%(asctime)s - %(name)s - %(levelname)s - %(message)s'
)
logger = logging.getLogger(__name__)

app = Flask(__name__)
app.secret_key = os.getenv('FLASK_SECRET_KEY', 'dev-secret-key')

# Initialize embed service
embed_service = PowerBIEmbedService()

@app.route('/')
def index():
    """Render the main page"""
    return render_template('index.html')

@app.route('/api/embed-token', methods=['POST'])
def get_embed_token():
    """API endpoint to get Power BI embed token"""
    try:
        data = request.get_json()
        
        # Validate required parameters
        user_email = data.get('userEmail')
        report_id = data.get('reportId')
        workspace_id = data.get('workspaceId')
        
        if not user_email:
            return jsonify({'error': 'userEmail is required'}), 400
        if not report_id:
            return jsonify({'error': 'reportId is required'}), 400
        if not workspace_id:
            return jsonify({'error': 'workspaceId is required'}), 400
        
        logger.info(
            f'Embed token request: User={user_email}, Report={report_id}'
        )
        
        # Get embed token
        embed_data = embed_service.get_embed_token(
            user_email,
            report_id,
            workspace_id
        )
        
        return jsonify(embed_data), 200
        
    except Exception as e:
        logger.error(f'Error in /api/embed-token: {str(e)}')
        return jsonify({
            'error': 'Failed to get embed token',
            'message': str(e)
        }), 500

@app.route('/health')
def health():
    """Health check endpoint"""
    return jsonify({'status': 'ok'}), 200

if __name__ == '__main__':
    port = int(os.getenv('PORT', 5000))
    debug = os.getenv('FLASK_ENV') == 'development'
    app.run(host='0.0.0.0', port=port, debug=debug)


STEP 5: CREATE THE FRONTEND TEMPLATE
-------------------------------------
Create a new folder: templates/
Create a new file: templates/index.html

<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Power BI Embed</title>
    <script src="https://cdn.jsdelivr.net/npm/powerbi-client@2.23.0/dist/powerbi.min.js">
    </script>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 20px;
            background-color: #f5f5f5;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .form-group {
            margin-bottom: 15px;
        }
        label {
            display: block;
            margin-bottom: 5px;
            font-weight: bold;
        }
        input {
            width: 100%;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            box-sizing: border-box;
        }
        button {
            background-color: #0078d4;
            color: white;
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
        }
        button:hover {
            background-color: #005a9e;
        }
        button:disabled {
            background-color: #ccc;
            cursor: not-allowed;
        }
        #embedContainer {
            margin-top: 20px;
            height: 600px;
            border: 1px solid #ddd;
            border-radius: 4px;
        }
        .error {
            color: #d13438;
            background-color: #fef0f0;
            padding: 10px;
            border-radius: 4px;
            margin-top: 10px;
        }
        .loading {
            text-align: center;
            padding: 20px;
            color: #666;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>Power BI Embedded Report</h1>
        
        <div class="form-group">
            <label for="userEmail">User Email:</label>
            <input type="email" id="userEmail" 
                   placeholder="user@example.com" required>
        </div>

        <div class="form-group">
            <label for="workspaceId">Workspace ID:</label>
            <input type="text" id="workspaceId" 
                   placeholder="12345678-1234-1234-1234-123456789abc" required>
        </div>

        <div class="form-group">
            <label for="reportId">Report ID:</label>
            <input type="text" id="reportId" 
                   placeholder="87654321-4321-4321-4321-cba987654321" required>
        </div>

        <button id="loadButton" onclick="loadReport()">Load Report</button>

        <div id="error" class="error" style="display:none;"></div>
        <div id="loading" class="loading" style="display:none;">
            Loading report...
        </div>
        <div id="embedContainer" style="display:none;"></div>
    </div>

    <script>
        async function loadReport() {
            const userEmail = document.getElementById('userEmail').value;
            const workspaceId = document.getElementById('workspaceId').value;
            const reportId = document.getElementById('reportId').value;
            const loadButton = document.getElementById('loadButton');
            const errorDiv = document.getElementById('error');
            const loadingDiv = document.getElementById('loading');
            const embedContainer = document.getElementById('embedContainer');

            if (!userEmail || !workspaceId || !reportId) {
                showError('Please fill in all fields');
                return;
            }

            errorDiv.style.display = 'none';
            loadingDiv.style.display = 'block';
            embedContainer.style.display = 'none';
            loadButton.disabled = true;

            try {
                console.log('Requesting embed token...');

                const response = await fetch('/api/embed-token', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        userEmail: userEmail,
                        reportId: reportId,
                        workspaceId: workspaceId
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || \`HTTP \${response.status}\`);
                }

                const embedData = await response.json();
                console.log('Embed token received');

                loadingDiv.style.display = 'none';
                embedContainer.style.display = 'block';

                const models = window['powerbi-client'].models;
                const config = {
                    type: 'report',
                    tokenType: models.TokenType.Embed,
                    accessToken: embedData.embedToken,
                    embedUrl: embedData.embedUrl,
                    id: embedData.reportId,
                    permissions: models.Permissions.Read,
                    settings: {
                        panes: {
                            filters: { visible: true },
                            pageNavigation: { visible: true }
                        }
                    }
                };

                const powerbi = window.powerbi;
                const report = powerbi.embed(embedContainer, config);

                report.on('loaded', () => {
                    console.log('Report loaded successfully');
                });

                report.on('error', (event) => {
                    console.error('Report error:', event.detail);
                    showError(\`Report error: \${event.detail.message}\`);
                });

            } catch (error) {
                console.error('Error loading report:', error);
                showError(\`Failed to load report: \${error.message}\`);
                loadingDiv.style.display = 'none';
            } finally {
                loadButton.disabled = false;
            }
        }

        function showError(message) {
            const errorDiv = document.getElementById('error');
            errorDiv.textContent = message;
            errorDiv.style.display = 'block';
        }
    </script>
</body>
</html>


STEP 6: CREATE FOLDER STRUCTURE
--------------------------------
Your project structure should look like:

project/
??? app.py
??? .env
??? .gitignore
??? requirements.txt
??? services/
?   ??? __init__.py
?   ??? powerbi_embed_service.py
??? templates/
    ??? index.html


Create services/__init__.py (empty file):
  touch services/__init__.py

Create .gitignore:
  .env
  __pycache__/
  *.pyc
  .vscode/
  .idea/


STEP 7: RUN YOUR APPLICATION
-----------------------------
Install dependencies:

  pip install -r requirements.txt

Run the application:

  python app.py

Or with Flask environment variable:

  export FLASK_APP=app.py
  export FLASK_ENV=development
  flask run

Open browser to: http://localhost:5000


CONFIGURATION CHECKLIST
-----------------------
Before running your application, verify:

* Python 3.8+ installed
* All pip packages installed
* .env file created with all required values
* .env file added to .gitignore
* Client Secret stored securely (not in source control)
* services/__init__.py file created
* templates folder with index.html created
* CORS origin added in Management Portal (http://localhost:5000)
* User email exists and has license in MerlinIQ system
* Report is assigned to the user in MerlinIQ system


TROUBLESHOOTING
---------------
ERROR: "App not registered for embedding"
  - Verify your Client ID matches the one in Management Portal
  - Check that embedding is enabled in the Customer Embedding tab

ERROR: "User not authorized"
  - Verify the user email exists in the Users tab
  - Check that the user has an active license
  - Ensure the report is assigned to the user

ERROR: "Failed to acquire access token"
  - Verify POWERBI_TENANT_ID, CLIENT_ID, and CLIENT_SECRET are correct
  - Check that API_SCOPE matches the Management Portal value

ERROR: "CORS error" in browser
  - Add http://localhost:5000 to CORS Configuration in Management Portal
  - For production, add your actual domain

ERROR: ImportError or ModuleNotFoundError
  - Verify all packages are installed: pip install -r requirements.txt
  - Check that services/__init__.py exists


SECURITY BEST PRACTICES
------------------------
* Store secrets in .env file
* Add .env to .gitignore
* Use Azure Key Vault or AWS Secrets Manager in production
* NEVER expose Client Secret in browser/client-side code
* Always call embed API from your backend server
* Implement rate limiting (use Flask-Limiter)
* Use HTTPS in production
* Implement proper error handling and logging
* Regenerate Client Secret regularly (every 6-12 months)


DEPLOYMENT OPTIONS
------------------
Azure App Service:
  - Use Application Settings for environment variables
  - Enable HTTPS/SSL
  - Use requirements.txt for dependencies

AWS Elastic Beanstalk:
  - Use Environment Properties for secrets
  - Configure HTTPS with ACM certificate

Docker:
  - Create Dockerfile
  - Use secrets management
  - Never bake secrets into images

Heroku:
  - Use Config Vars for environment variables
  - Add Procfile: web: gunicorn app:app


NEXT STEPS
----------
1. Add user authentication to your application
2. Implement token caching (tokens valid for ~1 hour)
3. Add token refresh logic before expiration
4. Create a report selection UI
5. Deploy to production
6. Monitor API usage and performance


SUPPORT
-------
For additional help:
- Review Management Portal Help tab
- Check Flask console logs for detailed errors
- Contact your MerlinIQ administrator


???????????????????????????????????????????????????????????????????????????????
Generated by MerlinIQ Management Portal - Customer Embedding Configuration
???????????????????????????????????????????????????????????????????????????????
`
  });
  
  const generateJavaScriptGuide = () => ({
    title: 'JavaScript-HTML',
    content: `
================================================================================
                    Power BI Embedding Integration Guide
                  Pure JavaScript/HTML Implementation
                      (With Backend Proxy Required)
================================================================================

OVERVIEW
--------
This guide shows how to embed Power BI reports using HTML and JavaScript.
IMPORTANT: You MUST have a backend server to handle authentication securely.
Client secrets cannot be safely stored in browser JavaScript.

PREREQUISITES
-------------
* Backend server (Node.js, Python, .NET, or any other)
* Azure Entra ID App Registration (created via Management Portal)
* Client ID and Client Secret (obtained from Management Portal)
* Web server to serve HTML files (for CORS)


ARCHITECTURE OVERVIEW
---------------------
+----------+       +--------------+       +-----------------+
| Browser  | <---> | Your Backend | <---> | Embed Token API |
|(Frontend)|       |   Server     |       |   (MerlinIQ)    |
+----------+       +--------------+       +-----------------+
     |
     +--------------------> Power BI Service
                           (Embed report with token)

Your browser calls YOUR backend API, which:
1. Authenticates with Azure Entra ID using Client Secret
2. Calls MerlinIQ Embed Token API
3. Returns embed token to browser
4. Browser embeds report using Power BI JavaScript SDK


>>> CRITICAL SECURITY WARNING
------------------------------
NEVER put Client ID and Client Secret directly in browser JavaScript!
This code assumes you have a backend API endpoint that handles authentication.


STEP 1: CREATE BACKEND API ENDPOINT
------------------------------------
You need a backend endpoint that returns embed tokens. Example endpoint:

  POST /api/embed-token
  
  Request Body:
  {
    "userEmail": "user@example.com",
    "reportId": "12345678-1234-1234-1234-123456789abc",
    "workspaceId": "87654321-4321-4321-4321-cba987654321"
  }
  
  Response:
  {
    "embedToken": "H4sIAA...",
    "embedUrl": "https://app.powerbi.com/reportEmbed?...",
    "reportId": "12345678-1234-1234-1234-123456789abc",
    "reportName": "Sales Report",
    "tokenExpiration": "2024-01-15T10:30:00Z"
  }

See the Node.js, Python, or C# guides for backend implementation examples.


STEP 2: ADD POWER BI CLIENT LIBRARY
------------------------------------
Include the Power BI JavaScript SDK in your HTML:

<script src="https://cdn.jsdelivr.net/npm/powerbi-client@2.23.0/dist/powerbi.min.js">
</script>

Or install via npm:

  npm install powerbi-client


STEP 3: CREATE THE HTML PAGE
-----------------------------
Create a new file: powerbi-embed.html

<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Power BI Embedded Report</title>
    
    <!-- Power BI Client SDK -->
    <script src="https://cdn.jsdelivr.net/npm/powerbi-client@2.23.0/dist/powerbi.min.js">
    </script>
    
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background: #f5f7fa;
            padding: 20px;
        }
        
        .container {
            max-width: 1400px;
            margin: 0 auto;
            background: white;
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            overflow: hidden;
        }
        
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
        }
        
        .header h1 {
            margin: 0;
            font-size: 28px;
        }
        
        .content {
            padding: 30px;
        }
        
        .form-section {
            background: #f8f9fa;
            padding: 25px;
            border-radius: 8px;
            margin-bottom: 25px;
        }
        
        .form-group {
            margin-bottom: 20px;
        }
        
        .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
            color: #333;
        }
        
        .form-group input {
            width: 100%;
            padding: 12px;
            border: 2px solid #e0e0e0;
            border-radius: 6px;
            font-size: 14px;
            transition: border-color 0.3s;
        }
        
        .form-group input:focus {
            outline: none;
            border-color: #667eea;
        }
        
        .form-group small {
            display: block;
            margin-top: 5px;
            color: #666;
            font-size: 12px;
        }
        
        .btn {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 14px 28px;
            border: none;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
        
        .btn:disabled {
            background: #ccc;
            cursor: not-allowed;
            transform: none;
        }
        
        .alert {
            padding: 15px 20px;
            border-radius: 6px;
            margin-bottom: 20px;
            display: none;
        }
        
        .alert-error {
            background: #fee2e2;
            color: #991b1b;
            border-left: 4px solid #dc2626;
        }
        
        .alert-info {
            background: #dbeafe;
            color: #1e40af;
            border-left: 4px solid #3b82f6;
        }
        
        .loading {
            text-align: center;
            padding: 40px;
            display: none;
        }
        
        .loading-spinner {
            border: 4px solid #f3f3f3;
            border-top: 4px solid #667eea;
            border-radius: 50%;
            width: 50px;
            height: 50px;
            animation: spin 1s linear infinite;
            margin: 0 auto 15px;
        }
        
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        
        #embedContainer {
            width: 100%;
            height: 700px;
            border: 2px solid #e0e0e0;
            border-radius: 8px;
            display: none;
        }
        
        .powered-by {
            text-align: center;
            padding: 20px;
            color: #666;
            font-size: 14px;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16" style="display: inline-block; vertical-align: middle; margin-right: 10px;">
                <path d="M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
                <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 0a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2z"/>
            </svg> Power BI Embedded Reports</h1>
            <p>Load and view your Power BI reports securely</p>
        </div>
        
        <div class="content">
            <!-- Alert Messages -->
            <div id="errorAlert" class="alert alert-error"></div>
            <div id="infoAlert" class="alert alert-info"></div>
            
            <!-- Configuration Form -->
            <div class="form-section">
                <h2 style="margin-bottom: 20px;">Report Configuration</h2>
                
                <div class="form-group">
                    <label for="userEmail">User Email Address</label>
                    <input type="email" 
                           id="userEmail" 
                           placeholder="user@example.com"
                           required>
                    <small>Email address of the user accessing the report</small>
                </div>
                
                <div class="form-group">
                    <label for="workspaceId">Workspace ID (GUID)</label>
                    <input type="text" 
                           id="workspaceId"
                           placeholder="12345678-1234-1234-1234-123456789abc"
                           required>
                    <small>Power BI workspace GUID from Management Portal</small>
                </div>
                
                <div class="form-group">
                    <label for="reportId">Report ID (GUID)</label>
                    <input type="text" 
                           id="reportId"
                           placeholder="87654321-4321-4321-4321-cba987654321"
                           required>
                    <small>Power BI report GUID from Management Portal</small>
                </div>
                
                <button id="loadButton" class="btn" onclick="loadReport()">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" style="display: inline-block; vertical-align: middle; margin-right: 5px;">
                        <path d="M8.5 11.5a.5.5 0 0 1-1 0V6.707L5.354 8.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 6.707V11.5z"/>
                        <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z"/>
                    </svg> Load Report
                </button>
            </div>
            
            <!-- Loading Indicator -->
            <div id="loading" class="loading">
                <div class="loading-spinner"></div>
                <p>Loading your report...</p>
            </div>
            
            <!-- Report Container -->
            <div id="embedContainer"></div>
        </div>
        
        <div class="powered-by">
            Powered by MerlinIQ Power BI Embedding
        </div>
    </div>

    <script>
        // ======================================================================
        // CONFIGURATION - Update this with your backend API endpoint
        // ======================================================================
        const CONFIG = {
            // Your backend API endpoint that returns embed tokens
            // This endpoint must handle authentication with Client Secret
            backendApiUrl: '/api/embed-token'  // UPDATE THIS!
            
            // Examples:
            // backendApiUrl: 'https://your-api.azurewebsites.net/api/embed-token'
            // backendApiUrl: 'http://localhost:3000/api/embed-token'
        };

        // ======================================================================
        // Global variables
        // ======================================================================
        let currentReport = null;

        // ======================================================================
        // Main function to load Power BI report
        // ======================================================================
        async function loadReport() {
            console.log('=== Starting Report Load Process ===');
            
            // Get form values
            const userEmail = document.getElementById('userEmail').value.trim();
            const workspaceId = document.getElementById('workspaceId').value.trim();
            const reportId = document.getElementById('reportId').value.trim();
            
            // Validate inputs
            if (!userEmail || !workspaceId || !reportId) {
                showError('Please fill in all required fields');
                return;
            }
            
            if (!isValidEmail(userEmail)) {
                showError('Please enter a valid email address');
                return;
            }
            
            if (!isValidGuid(workspaceId) || !isValidGuid(reportId)) {
                showError('Workspace ID and Report ID must be valid GUIDs');
                return;
            }
            
            // Show loading state
            setLoadingState(true);
            hideAllAlerts();
            
            try {
                // Step 1: Get embed token from YOUR backend
                console.log('Step 1: Requesting embed token from backend...');
                const embedData = await getEmbedTokenFromBackend(
                    userEmail, 
                    reportId, 
                    workspaceId
                );
                
                console.log('Embed token received successfully');
                
                // Step 2: Embed the report
                console.log('Step 2: Embedding Power BI report...');
                await embedPowerBIReport(embedData);
                
                console.log('Report embedded successfully');
                showInfo('Report loaded successfully!');
                
            } catch (error) {
                console.error('Error loading report:', error);
                showError(\`Failed to load report: \${error.message}\`);
            } finally {
                setLoadingState(false);
            }
        }

        // ======================================================================
        // Call backend API to get embed token
        // ======================================================================
        async function getEmbedTokenFromBackend(userEmail, reportId, workspaceId) {
            const response = await fetch(CONFIG.backendApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userEmail: userEmail,
                    reportId: reportId,
                    workspaceId: workspaceId
                })
            });
            
            if (!response.ok) {
                let errorMessage = \`HTTP \${response.status}\`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (e) {
                    errorMessage = await response.text() || errorMessage;
                }
                throw new Error(errorMessage);
            }
            
            const embedData = await response.json();
            
            // Validate response
            if (!embedData.embedToken || !embedData.embedUrl || !embedData.reportId) {
                throw new Error('Invalid response from backend API');
            }
            
            return embedData;
        }

        // ======================================================================
        // Embed Power BI report using SDK
        // ======================================================================
        async function embedPowerBIReport(embedData) {
            const embedContainer = document.getElementById('embedContainer');
            
            // Clear any existing report
            if (currentReport) {
                try {
                    currentReport.remove();
                } catch (e) {
                    console.warn('Error removing previous report:', e);
                }
                currentReport = null;
            }
            
            // Get Power BI models
            const models = window['powerbi-client'].models;
            
            // Configure embed settings
            const config = {
                type: 'report',
                tokenType: models.TokenType.Embed,
                accessToken: embedData.embedToken,
                embedUrl: embedData.embedUrl,
                id: embedData.reportId,
                permissions: models.Permissions.Read,
                settings: {
                    panes: {
                        filters: {
                            expanded: false,
                            visible: true
                        },
                        pageNavigation: {
                            visible: true
                        }
                    },
                    background: models.BackgroundType.Transparent,
                    layoutType: models.LayoutType.Custom,
                    customLayout: {
                        displayOption: models.DisplayOption.FitToPage
                    }
                }
            };
            
            console.log('Embedding with configuration:', {
                reportId: embedData.reportId,
                reportName: embedData.reportName,
                tokenLength: embedData.embedToken.length
            });
            
            // Embed the report
            const powerbi = window.powerbi;
            currentReport = powerbi.embed(embedContainer, config);
            
            // Show container
            embedContainer.style.display = 'block';
            
            // Set up event handlers
            currentReport.off('loaded');
            currentReport.on('loaded', function() {
                console.log('? Report loaded event fired');
            });
            
            currentReport.off('rendered');
            currentReport.on('rendered', function() {
                console.log('? Report rendered event fired');
            });
            
            currentReport.off('error');
            currentReport.on('error', function(event) {
                console.error('? Report error event:', event.detail);
                
                let errorMsg = 'An error occurred while loading the report';
                if (event.detail) {
                    errorMsg += \`: \${event.detail.message || event.detail.detailedMessage || 'Unknown error'}\`;
                }
                
                showError(errorMsg);
            });
        }

        // ======================================================================
        // Helper functions
        // ======================================================================
        
        function setLoadingState(isLoading) {
            const loadButton = document.getElementById('loadButton');
            const loading = document.getElementById('loading');
            const embedContainer = document.getElementById('embedContainer');
            
            loadButton.disabled = isLoading;
            loading.style.display = isLoading ? 'block' : 'none';
            
            if (isLoading) {
                embedContainer.style.display = 'none';
            }
        }
        
        function showError(message) {
            const errorAlert = document.getElementById('errorAlert');
            errorAlert.textContent = message;
            errorAlert.style.display = 'block';
            
            // Scroll to alert
            errorAlert.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        
        function showInfo(message) {
            const infoAlert = document.getElementById('infoAlert');
            infoAlert.textContent = message;
            infoAlert.style.display = 'block';
            
            // Auto-hide after 3 seconds
            setTimeout(() => {
                infoAlert.style.display = 'none';
            }, 3000);
        }
        
        function hideAllAlerts() {
            document.getElementById('errorAlert').style.display = 'none';
            document.getElementById('infoAlert').style.display = 'none';
        }
        
        function isValidEmail(email) {
            const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
            return emailRegex.test(email);
        }
        
        function isValidGuid(guid) {
            const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            return guidRegex.test(guid);
        }
        
        // ======================================================================
        // Initialize on page load
        // ======================================================================
        document.addEventListener('DOMContentLoaded', function() {
            console.log('Page loaded - Power BI Embed ready');
            console.log('Using backend API:', CONFIG.backendApiUrl);
        });
    </script>
</body>
</html>


STEP 4: CONFIGURE BACKEND API URL
----------------------------------
In the HTML file, update the CONFIG object with your backend API endpoint:

const CONFIG = {
    backendApiUrl: 'https://your-backend-api.com/api/embed-token'
};

Examples:
- Azure: 'https://your-app.azurewebsites.net/api/embed-token'
- AWS: 'https://your-api.execute-api.region.amazonaws.com/prod/embed-token'
- Local: 'http://localhost:3000/api/embed-token'


STEP 5: DEPLOY AND TEST
------------------------
1. Serve your HTML file from a web server (for CORS)
2. Add your domain to CORS Configuration in Management Portal
3. Open the page in a browser
4. Enter user email, workspace ID, and report ID
5. Click "Load Report"


FINDING WORKSPACE AND REPORT IDs
---------------------------------
1. Log into Management Portal
2. Navigate to Reports tab
3. Click the "Embed" button next to the report you want to embed
4. Copy the Workspace ID and Report ID from the popup


CONFIGURATION CHECKLIST
-----------------------
* Backend API endpoint implemented and deployed
* CONFIG.backendApiUrl updated in HTML file
* Backend API CORS configured to allow your frontend domain
* Management Portal CORS configured with your frontend domain
* Web server configured to serve HTML file
* User email exists and has license in MerlinIQ system
* Report is assigned to the user in MerlinIQ system


TROUBLESHOOTING
---------------
ERROR: "Failed to fetch" or CORS error
  - Add your domain to CORS Configuration in Management Portal
  - Ensure backend API allows CORS from your domain
  - Check browser console for specific CORS error details

ERROR: 404 or "Endpoint not found"
  - Verify CONFIG.backendApiUrl is correct
  - Test backend endpoint directly (Postman, curl)
  - Check backend server is running and accessible

ERROR: "Invalid response from backend API"
  - Ensure backend returns all required fields (embedToken, embedUrl, reportId)
  - Check backend logs for errors
  - Test backend endpoint manually

ERROR: "Report error" after embedding
  - Check that embed token is valid (not expired)
  - Verify workspace and report IDs are correct
  - Check browser console for detailed Power BI errors


SECURITY BEST PRACTICES
------------------------
* NEVER put Client Secret in browser JavaScript
* Always call embed API from YOUR backend server
* Implement authentication on your backend API
* Use HTTPS for all production deployments
* Validate user authorization on YOUR backend before returning tokens
* Implement rate limiting on backend API
* Add CORS restrictions to limit allowed origins
* Monitor API usage and set up alerts


ADVANCED FEATURES
-----------------
Token Refresh:
- Embed tokens expire after ~1 hour
- Implement token refresh before expiration
- Use report.setAccessToken(newToken) method

Report Interactions:
- Get/set filters: report.getFilters(), report.setFilters()
- Navigate pages: report.getPages(), report.setPage()
- Get selections: report.on('dataSelected', handler)

Full Screen:
- report.fullscreen()

Export:
- report.exportData(ExportDataType.Summarized)


NEXT STEPS
----------
1. Implement user authentication on your frontend
2. Create backend API with proper security
3. Add token refresh logic
4. Implement report selection UI
5. Add error handling and user feedback
6. Deploy to production with HTTPS
7. Monitor usage and performance


SUPPORT
-------
For additional help:
- Review Management Portal Help tab
- Check browser console for errors
- Check backend server logs
- Contact your MerlinIQ administrator


================================================================================
Generated by MerlinIQ Management Portal - Customer Embedding Configuration
================================================================================
`
  });
  
  const examples = {
    csharp: {
      title: '.NET (C#)',
      code: `// PowerBIEmbedService.cs
using Microsoft.Identity.Client;
using System.Net.Http.Headers;

public class PowerBIEmbedService
{
    private readonly IConfiguration _configuration;
    private readonly IHttpClientFactory _httpClientFactory;

    public PowerBIEmbedService(
        IConfiguration configuration,
        IHttpClientFactory httpClientFactory)
    {
        _configuration = configuration;
        _httpClientFactory = httpClientFactory;
    }

    public async Task<EmbedTokenResponse> GetEmbedTokenAsync(
        string userEmail, Guid reportId, Guid workspaceId)
    {
        var config = _configuration.GetSection("PowerBIEmbed");
        
        // Step 1: Get access token from Entra ID
        var app = ConfidentialClientApplicationBuilder
            .Create(config["ClientId"])
            .WithClientSecret(config["ClientSecret"])
            .WithAuthority(new Uri(
                $"https://login.microsoftonline.com/{config["TenantId"]}"))
            .Build();

        var authResult = await app.AcquireTokenForClient(
            new[] { config["ApiScope"] }).ExecuteAsync();

        // Step 2: Call embed token API
        // Note: No subscriptionId needed - mapped from your client ID
        var httpClient = _httpClientFactory.CreateClient();
        httpClient.DefaultRequestHeaders.Authorization =
            new AuthenticationHeaderValue("Bearer", authResult.AccessToken);

        var request = new { userEmail, reportId, workspaceId };
        var response = await httpClient.PostAsJsonAsync(
            config["EmbedApiEndpoint"], request);

        response.EnsureSuccessStatusCode();
        return await response.Content.ReadFromJsonAsync<EmbedTokenResponse>();
    }
}

// appsettings.json Configuration:
{
  "PowerBIEmbed": {
    "TenantId": "${tenantId || 'YOUR_TENANT_ID'}",
    "ClientId": "${clientId || 'YOUR_CLIENT_ID'}",
    "ClientSecret": "YOUR_SECRET_FROM_MANAGEMENT_PORTAL",
    "ApiScope": "${fullApiScope}",
    "EmbedApiEndpoint": "${apiEndpoint}"
  }
}

// WARNING: Store ClientSecret in Azure Key Vault or User Secrets in production!`
    },
    nodejs: {
      title: 'Node.js',
      code: `// powerBIEmbedService.js
const msal = require('@azure/msal-node');
const axios = require('axios');

class PowerBIEmbedService {
    constructor() {
        this.msalConfig = {
            auth: {
                clientId: process.env.POWERBI_CLIENT_ID,
                authority: \`https://login.microsoftonline.com/\${process.env.POWERBI_TENANT_ID}\`,
                clientSecret: process.env.POWERBI_CLIENT_SECRET
            }
        };
        this.cca = new msal.ConfidentialClientApplication(this.msalConfig);
    }

    async getEmbedToken(userEmail, reportId, workspaceId) {
        // Step 1: Get access token from Entra ID
        const authResponse = await this.cca.acquireTokenByClientCredential({
            scopes: [process.env.POWERBI_API_SCOPE]
        });
        
        // Step 2: Call embed token API
        // Note: No subscriptionId needed - mapped from your client ID
        const response = await axios.post(
            process.env.POWERBI_EMBED_API_ENDPOINT,
            { userEmail, reportId, workspaceId },
            {
                headers: {
                    'Authorization': \`Bearer \${authResponse.accessToken}\`,
                    'Content-Type': 'application/json'
                }
            }
        );
        
        return response.data;
    }
}

// .env Configuration:
POWERBI_TENANT_ID=${tenantId || 'YOUR_TENANT_ID'}
POWERBI_CLIENT_ID=${clientId || 'YOUR_CLIENT_ID'}
POWERBI_CLIENT_SECRET=YOUR_SECRET_FROM_MANAGEMENT_PORTAL
POWERBI_API_SCOPE=${fullApiScope}
POWERBI_EMBED_API_ENDPOINT=${apiEndpoint}

// WARNING: Add .env to .gitignore - Never commit secrets!`
    },
    python: {
      title: 'Python',
      code: `# powerbi_embed_service.py
from msal import ConfidentialClientApplication
import requests
import os

class PowerBIEmbedService:
    def __init__(self):
        self.tenant_id = os.getenv('POWERBI_TENANT_ID')
        self.client_id = os.getenv('POWERBI_CLIENT_ID')
        self.client_secret = os.getenv('POWERBI_CLIENT_SECRET')
        self.api_scope = os.getenv('POWERBI_API_SCOPE')
        self.embed_api_endpoint = os.getenv('POWERBI_EMBED_API_ENDPOINT')
        
        self.authority = f'https://login.microsoftonline.com/{self.tenant_id}'
        self.app = ConfidentialClientApplication(
            client_id=self.client_id,
            client_credential=self.client_secret,
            authority=self.authority
        )
    
    def get_embed_token(self, user_email: str, report_id: str, 
                       workspace_id: str) -> dict:
        # Step 1: Get access token from Entra ID
        token_response = self.app.acquire_token_for_client(
            scopes=[self.api_scope]
        )
        
        if 'access_token' not in token_response:
            raise Exception('Failed to acquire token')
        
        # Step 2: Call embed token API
        # Note: No subscriptionId needed - mapped from your client ID
        headers = {
            'Authorization': f"Bearer {token_response['access_token']}",
            'Content-Type': 'application/json'
        }
        
        response = requests.post(
            self.embed_api_endpoint,
            json={
                'userEmail': user_email,
                'reportId': report_id,
                'workspaceId': workspace_id
            },
            headers=headers
        )
        
        response.raise_for_status()
        return response.json()

# .env Configuration:
POWERBI_TENANT_ID=${tenantId || 'YOUR_TENANT_ID'}
POWERBI_CLIENT_ID=${clientId || 'YOUR_CLIENT_ID'}
POWERBI_CLIENT_SECRET=YOUR_SECRET_FROM_MANAGEMENT_PORTAL
POWERBI_API_SCOPE=${fullApiScope}
POWERBI_EMBED_API_ENDPOINT=${apiEndpoint}

# WARNING: Add .env to .gitignore - Never commit secrets!`
    },
    javascript: {
      title: 'HTML/JavaScript',
      code: `<!-- WARNING: IMPORTANT: This must call YOUR backend API -->
<!-- NEVER put Client Secret in browser JavaScript! -->

<!-- Add Power BI Client SDK -->
<script src="https://cdn.jsdelivr.net/npm/powerbi-client@2.23.0/dist/powerbi.min.js">
</script>

<div id="embedContainer" style="height: 600px;"></div>

<script>
async function loadReport() {
    try {
        // Call YOUR backend API to get embed token
        // Your backend handles authentication with Client Secret
        const response = await fetch('/api/embed-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userEmail: 'user@example.com',
                reportId: 'YOUR-REPORT-GUID',
                workspaceId: 'YOUR-WORKSPACE-GUID'
            })
        });
        
        const embedData = await response.json();
        
        // Embed the report using Power BI SDK
        const models = window['powerbi-client'].models;
        const config = {
            type: 'report',
            tokenType: models.TokenType.Embed,
            accessToken: embedData.embedToken,
            embedUrl: embedData.embedUrl,
            id: embedData.reportId,
            permissions: models.Permissions.Read,
            settings: {
                panes: {
                    filters: { visible: true },
                    pageNavigation: { visible: true }
                }
            }
        };
        
        const container = document.getElementById('embedContainer');
        const powerbi = window.powerbi;
        const report = powerbi.embed(container, config);
        
        report.on('loaded', () => console.log('Report loaded'));
        report.on('error', (event) => console.error('Error:', event.detail));
        
    } catch (error) {
        console.error('Failed to load report:', error);
    }
}

loadReport();
</script>

<!-- Backend API Endpoint Requirements:
POST /api/embed-token
Request: { userEmail, reportId, workspaceId }
Response: { embedToken, embedUrl, reportId, reportName, tokenExpiration }

WARNING: Backend must authenticate with Client Secret and call:
   ${apiEndpoint}
-->`
    }
  };

  return (
    <div style={{ marginTop: '1rem', border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Tab Headers */}
        <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', background: '#f8f9fa' }}>
        {Object.keys(examples).map((key) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: activeTab === key ? 'white' : 'transparent',
              borderBottom: activeTab === key ? '3px solid #1B394B' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === key ? 'bold' : 'normal',
              color: '#1B394B',
              transition: 'all 0.3s'
            }}
          >
            {examples[key].title}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div style={{ padding: '1.5rem', background: '#f8f9fa' }}>
        {/* Download Guide Button */}
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0, color: '#495057' }}>Code Example</h4>
          <button
            onClick={() => downloadPDFGuide(activeTab)}
            className="btn btn-secondary"
            style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', background: '#1B394B', border: '1px solid #1B394B', color: 'white' }}
          >
            Download Complete Guide (.txt)
          </button>
        </div>
        
        {/* Code Block */}
        <pre style={{ 
          margin: 0, 
          padding: '1.5rem', 
          background: '#f8f9fa', 
          color: '#495057', 
          borderRadius: '6px', 
          overflow: 'auto', 
          maxHeight: '500px',
          fontSize: '0.85rem',
          lineHeight: '1.6',
          border: '1px solid #dee2e6',
          fontFamily: 'Consolas, Monaco, "Courier New", monospace'
        }}>
          <code style={{ color: '#495057' }}>{examples[activeTab].code}</code>
        </pre>
        
        {/* Important Notes */}
        <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px' }}>
          <h5 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}><strong>Important Configuration Notes:</strong></h5>
          <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#856404', fontSize: '0.9rem' }}>
            <li><strong>Store secrets securely</strong> - Use Azure Key Vault, AWS Secrets Manager, or environment variables</li>
            <li><strong>NEVER commit secrets</strong> to source control or expose them in browser code</li>
            <li><strong>CORS Configuration</strong> - Add your domain to the CORS section above</li>
            <li><strong>User Authorization</strong> - User must exist in Users tab, have active license, and be assigned to the report</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CustomerEmbeddingManagement;

